import cx from 'classnames';
import {includes, isEmpty, isFunction, isNil, map} from 'lodash';
import {useCallback, useMemo} from 'react';
import {Link, LinkProps, NavLink} from 'react-router-dom';

import {Icon} from 'components';

import {MenuItemInfo} from '../types';

export const ACTIVE_CLASS_NAME = 'active';

type MenuItemWrapperProps = LinkProps & {
    componentChildren: React.ReactNode;
    activeClassName: string;
    wrapperClassName: string;
    activeWrapperClassName: string;
    noWrapLink?: boolean;
};
export const MenuItemWrapper: React.FC<MenuItemWrapperProps> = (
    {children, noWrapLink, wrapperClassName, activeWrapperClassName, componentChildren, ...rest}: MenuItemWrapperProps
) => {
    const active = useMemo(() => includes(rest.className, ACTIVE_CLASS_NAME), [rest]);
    const renderComponentChildren = useCallback(
        () => isFunction(componentChildren) ? componentChildren({active}) : componentChildren,
        [componentChildren, active]
    );
    return (
        <div className={cx({[activeWrapperClassName]: active, [wrapperClassName]: wrapperClassName})}>
            {noWrapLink ?
                renderComponentChildren() :
                <Link to={rest.href} {...rest}>
                    {renderComponentChildren()}
                </Link>}
            {active && (isFunction(children) ? children({active}) : children)}
        </div>
    );
};

type Props = MenuItemInfo;
export const MenuItem: React.FC<Props> = (({icon, badge, activeIcon, title, link, subItems}: Props) => (
    <NavLink
        to={link}
        className='tw-text-dark tw-relative tw-block'
        activeClassName={`${ACTIVE_CLASS_NAME} tw-font-normal lg:tw-font-bold`}
        wrapperClassName='lg:tw-border-l-6 tw-border-0 tw-border-transparent'
        activeWrapperClassName='tw-border-primary'
        component={MenuItemWrapper}
        componentChildren={({active}) => (
            <div className='tw-flex tw-flex-col lg:tw-flex-row tw-items-center tw-py-0 lg:tw-py-4 tw-pl-0 lg:tw-pl-16 tw-relative'>
                <Icon
                    className='tw-mr-0 tw-relative tw-left-0 lg:tw-absolute lg:tw-mr-2 lg:tw-left-6'
                    name={active ? activeIcon : icon}
                    primary={active}
                    size='lg'
                />
                {!isNil(badge) &&
                    <div className={`tw-absolute tw-bg-alert-light tw-text-xs tw-text-light tw-py-0.5 tw-px-1.5 tw-rounded-full
                        tw-top-1.5 -lg:tw--top-0.5 tw-left-9 -lg:tw-left-5 -lg-landscape:tw-left-3`}
                    >
                        {badge}
                    </div>}
                <div className={cx('lg:tw-text-lg -lg-landscape:tw-hidden',
                    {'tw-text-primary': active, 'lg:tw-text-dark': active && !isEmpty(subItems)})}
                >
                    {title}
                </div>
            </div>
        )}
    >
        {!isEmpty(subItems) &&
            <div className='tw-hidden lg:tw-block tw-ml-16 tw-text-sm tw-text-dark tw-font-normal'>
                {map(subItems, (item) => item.activeOnly ? null : <NavLink
                    key={item.title}
                    exact
                    to={item.link}
                    className='tw-text-dark tw-text-current tw-block tw-mb-5'
                    activeClassName={ACTIVE_CLASS_NAME}
                    component={MenuItemWrapper}
                    componentChildren={({active}) =>
                        <span className={active ? 'tw-text-primary' : 'tw-text-dark'}>{item.title}</span>}
                />
                )}
            </div>}
    </NavLink>
));
